import type { Editor } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import { Link } from '@tiptap/extension-link';
import { StarterKit } from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { EditorToolbar, TOOLBAR_HEIGHT } from './EditorToolbar';
import { classNames } from '@framework/utils';
import { forwardRef, useEffect, useState } from 'react';
import type { DescriptionPanelFontSize } from '../../domain';
import './descriptionEditor.css';

type Props = {
  content: string;
  fontSize: DescriptionPanelFontSize;
  editing: boolean;
  className?: string;
  onBlur(): void;
  onSubmit(value: string): void;
  onFontSizeChange(fontSize: DescriptionPanelFontSize): void;
  onURLModalOpen(): void;
  onImageURLModalOpen(): void;
};

export const DescriptionEditor = forwardRef<Editor, Props>(
  ({ content, fontSize, className, onBlur, onSubmit, onFontSizeChange, onURLModalOpen, onImageURLModalOpen }, ref) => {
    const [toolbarHeight, setToolbarHeight] = useState<number>(TOOLBAR_HEIGHT);
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          link: false,
        }),
        Link,
        Image.configure({
          inline: true,
          allowBase64: true,
        }),
      ],
      editorProps: {
        attributes: {
          class: 'focus:outline-none size-full rounded-lg border border-gray-400 p-4 overflow-y-auto overflow-x-hidden',
        },
      },
      onCreate: ({ editor }) => {
        editor.commands.setContent(content);
      },
      onBlur: ({ event }) => {
        handleEditorBlur(event);
      },
    });

    useEffect(() => {
      if (!editor) {
        return;
      }

      if (!editor.isFocused && editor.getHTML() !== content) {
        // NOTE: setContent()するとカーソル位置が失われるので、contentプロップとエディタの description が異なる場合に、別のユーザからの変更があったとして更新する
        editor.commands.setContent(content);
      }
    }, [content, editor]);

    const handleEditorBlur = (e: React.FocusEvent | FocusEvent) => {
      // メニューボタンやセレクトボックスをクリックした場合は onBlur を発火させない
      if (
        e.relatedTarget instanceof HTMLElement &&
        (e.relatedTarget.nodeName === 'BUTTON' || e.relatedTarget.nodeName === 'SELECT')
      ) {
        return;
      }
      onSubmit(editor?.getHTML() ?? content);
      onBlur();
    };

    if (!editor) {
      return null;
    }

    if (editor && ref && typeof ref !== 'function') {
      ref.current = editor;
    }

    return (
      <div className={classNames(className)}>
        <EditorToolbar
          editor={editor}
          fontSize={fontSize}
          onFontSizeChange={onFontSizeChange}
          onURLModalOpen={onURLModalOpen}
          onImageURLModalOpen={onImageURLModalOpen}
          onChangeHeight={setToolbarHeight}
        />
        <div className="w-full" style={{ height: `calc(100% - ${toolbarHeight}px)` }}>
          <EditorContent editor={editor} className="size-full" />
        </div>
      </div>
    );
  }
);

DescriptionEditor.displayName = 'DescriptionEditor';
